/**
 * TaskTypeDropdown Component
 * A dropdown selector for TriSys TaskTypes with icon images
 * 
 * @description Provides a styled task type dropdown with:
 * - 16x16 task type icons from TriSys server
 * - Single column layout with vertical scrolling
 * - Thin scrollbar matching app styling
 * - Auto-populates from cached StandingData (blank if not available)
 * - onChange callback for selection handling
 * 
 * @example
 * ```typescript
 * const taskTypeDropdown = new TaskTypeDropdown(container, {
 *   name: 'taskType',
 *   label: 'Task Type',
 *   value: 'CV Send',
 *   onChange: (value, label) => console.log('Selected:', value, label)
 * });
 * await taskTypeDropdown.initialize();
 * ```
 * 
 * @requires Standing Data must be cached from TriSysWebAPI login
 *           If not cached, dropdown shows "Select Task Type..." with no options
 */

import { IComponent } from './IComponent';
import { trisysAPI } from '@/services/api/TriSysWebAPI';
import { Logger } from '@/services/Logger';

const logger = Logger.scope('TaskTypeDropdown');

/**
 * Task type data structure from StandingData
 */
export interface TaskTypeData {
  TaskTypeId: number;
  Name: string;
}

/**
 * Configuration options for TaskTypeDropdown
 */
export interface TaskTypeDropdownOptions {
  /** Unique name/id for the dropdown */
  name: string;
  /** Optional label displayed above the dropdown */
  label?: string;
  /** Currently selected task type name */
  value?: string;
  /** Placeholder text when nothing selected */
  placeholder?: string;
  /** Size variant */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Whether the dropdown is disabled */
  disabled?: boolean;
  /** Whether the dropdown is required */
  required?: boolean;
  /** CSS classes to add to the container */
  containerClass?: string;
  /** Callback when selection changes */
  onChange?: (value: string, label: string) => void;
}

/**
 * TaskTypeDropdown Component Class
 * A reusable dropdown for selecting TriSys TaskTypes with icons
 */
export class TaskTypeDropdown implements IComponent {
  private container: HTMLElement;
  private options: TaskTypeDropdownOptions;
  private taskTypes: TaskTypeData[] = [];
  private initialized = false;
  private selectedValue: string = '';
  private isOpen = false;
  private uniqueId: string;

  constructor(container: HTMLElement, options: TaskTypeDropdownOptions) {
    this.container = container;
    this.uniqueId = `tasktype-${options.name}-${Date.now()}`;
    this.options = {
      placeholder: 'Select Task Type...',
      size: 'sm',
      ...options
    };
    this.selectedValue = options.value || '';
  }

  /**
   * Initialize the component
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;

    // Load task types from cached standing data
    this.loadTaskTypes();
    
    this.render();
    this.bindEvents();
    this.initialized = true;
    
    logger.debug('TaskTypeDropdown initialized', {
      name: this.options.name,
      taskTypeCount: this.taskTypes.length,
      selectedValue: this.selectedValue
    });
  }

  /**
   * Load task types from cached StandingData
   * If not cached, taskTypes will be empty (dropdown shows placeholder only)
   */
  private loadTaskTypes(): void {
    try {
      const standingData = trisysAPI.getCachedStandingData();
      if (standingData) {
        // Access TaskTypes from standing data (may be under different property names)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const anyData = standingData as any;
        if (anyData.TaskTypes && Array.isArray(anyData.TaskTypes)) {
          this.taskTypes = anyData.TaskTypes as TaskTypeData[];
          logger.debug('Loaded TaskTypes from StandingData', { count: this.taskTypes.length });
        } else {
          logger.debug('No TaskTypes found in StandingData');
        }
      } else {
        logger.debug('No cached StandingData available');
      }
    } catch (error) {
      logger.warn('Error loading TaskTypes from StandingData', { error });
    }
  }

  /**
   * Get the image URL for a TaskType icon
   */
  private getTaskTypeImageUrl(taskTypeName: string): string {
    if (!taskTypeName) return '';
    const encodedName = encodeURIComponent(taskTypeName);
    return `https://apex.trisys.co.uk/images/trisys/16x16/tasktype/${encodedName}.png`;
  }

  /**
   * Render the component
   */
  private render(): void {
    const { label, placeholder, size, disabled, required, containerClass } = this.options;
    
    // Find selected task type
    const selectedTaskType = this.taskTypes.find(t => t.Name === this.selectedValue);
    const selectedLabel = selectedTaskType?.Name || placeholder || 'Select Task Type...';
    const selectedImageUrl = this.selectedValue ? this.getTaskTypeImageUrl(this.selectedValue) : '';
    
    // Size classes for button
    const sizeClass = size === 'xs' ? 'btn-xs' : size === 'sm' ? 'btn-sm' : size === 'lg' ? 'btn-lg' : '';
    
    // Build the dropdown HTML
    let html = '';
    
    if (label) {
      html += `
        <fieldset class="fieldset ${containerClass || ''}">
          <legend class="fieldset-legend">${label}${required ? ' <span class="text-error">*</span>' : ''}</legend>
      `;
    }
    
    html += `
      <div class="dropdown" id="${this.uniqueId}" data-tasktype-dropdown>
        <div tabindex="0" role="button" 
             class="btn ${sizeClass} btn-outline gap-2 min-w-48 justify-start ${disabled ? 'btn-disabled' : ''}" 
             data-action="toggle-dropdown"
             ${disabled ? 'disabled' : ''}>
          ${selectedImageUrl 
            ? `<img src="${selectedImageUrl}" alt="" class="w-4 h-4" onerror="this.style.display='none'">`
            : '<span class="w-4"></span>'}
          <span class="flex-1 text-left">${selectedLabel}</span>
          <i class="fas fa-chevron-down text-xs"></i>
        </div>
        <ul tabindex="0" class="dropdown-content dropdown-scrollable bg-base-100 rounded-box z-50 max-h-96 overflow-y-auto shadow-lg border border-base-300 p-1"
            style="display: flex; flex-direction: column; width: max-content;">
          <li class="list-none">
            <a class="flex items-center gap-2 px-2 py-1.5 text-sm whitespace-nowrap hover:bg-base-200 rounded cursor-pointer ${!this.selectedValue ? 'bg-primary/20' : ''}" 
               data-action="select-tasktype" data-value="">
              <span class="w-4"></span>
              <span>${placeholder || 'Select Task Type...'}</span>
            </a>
          </li>
          ${this.taskTypes.map(t => `
            <li class="list-none">
              <a class="flex items-center gap-2 px-2 py-1.5 text-sm whitespace-nowrap hover:bg-base-200 rounded cursor-pointer ${t.Name === this.selectedValue ? 'bg-primary/20' : ''}" 
                 data-action="select-tasktype" data-value="${this.escapeHtml(t.Name)}">
                <img src="${this.getTaskTypeImageUrl(t.Name)}" alt="" class="w-4 h-4" onerror="this.style.display='none'">
                <span>${this.escapeHtml(t.Name)}</span>
              </a>
            </li>
          `).join('')}
        </ul>
      </div>
    `;
    
    if (label) {
      html += '</fieldset>';
    }
    
    this.container.innerHTML = html;
  }

  /**
   * Escape HTML to prevent XSS
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Bind event handlers
   */
  private bindEvents(): void {
    this.container.addEventListener('click', this.handleClick.bind(this));
  }

  /**
   * Handle click events
   */
  private handleClick(e: Event): void {
    const target = e.target as HTMLElement;
    const actionElement = target.closest('[data-action]') as HTMLElement;
    if (!actionElement) return;

    const action = actionElement.dataset.action;

    switch (action) {
      case 'select-tasktype':
        e.preventDefault();
        const value = actionElement.dataset.value || '';
        this.setValue(value);
        // Close dropdown by removing focus
        const dropdown = this.container.querySelector('.dropdown');
        if (dropdown) {
          (dropdown.querySelector('[tabindex]') as HTMLElement)?.blur();
        }
        break;
    }
  }

  /**
   * Set the selected value
   */
  setValue(value: string): void {
    const oldValue = this.selectedValue;
    this.selectedValue = value;
    
    // Find the label for the callback
    const taskType = this.taskTypes.find(t => t.Name === value);
    const label = taskType?.Name || '';
    
    // Re-render to update display
    this.render();
    this.bindEvents();
    
    // Trigger callback
    if (this.options.onChange && value !== oldValue) {
      this.options.onChange(value, label);
    }
    
    logger.debug('TaskType selected', { value, label });
  }

  /**
   * Get the current value
   */
  getValue(): string {
    return this.selectedValue;
  }

  /**
   * Get the current label
   */
  getLabel(): string {
    const taskType = this.taskTypes.find(t => t.Name === this.selectedValue);
    return taskType?.Name || '';
  }

  /**
   * Get the TaskTypeId for the selected value
   */
  getTaskTypeId(): number | null {
    const taskType = this.taskTypes.find(t => t.Name === this.selectedValue);
    return taskType?.TaskTypeId || null;
  }

  /**
   * Refresh task types from StandingData
   * Call this if standing data is loaded after component initialization
   */
  refresh(): void {
    this.loadTaskTypes();
    this.render();
    this.bindEvents();
  }

  /**
   * Enable the dropdown
   */
  enable(): void {
    this.options.disabled = false;
    this.render();
    this.bindEvents();
  }

  /**
   * Disable the dropdown
   */
  disable(): void {
    this.options.disabled = true;
    this.render();
    this.bindEvents();
  }

  /**
   * Check if task types are loaded
   */
  hasTaskTypes(): boolean {
    return this.taskTypes.length > 0;
  }

  /**
   * Get all available task types
   */
  getTaskTypes(): TaskTypeData[] {
    return [...this.taskTypes];
  }

  /**
   * Destroy the component and clean up
   */
  destroy(): void {
    this.container.innerHTML = '';
    this.initialized = false;
  }
}
